import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { ApiError } from '../../lib/api.js';
import type { FormPromptDraft } from '../../lib/form-prompt-api.js';
import { t } from '../../lib/i18n.js';

/**
 * "Describe it in a sentence" for any form (pass 8) — the App 2 panel's rules,
 * for a record the server numbers: it fills in the form, never saves; it says
 * what it filled and asks about what it could not; it lists figures the
 * sentence did not give, so nobody mistakes an empty box for a zero.
 */
export function FormPromptPanel({
  id,
  heading,
  label,
  placeholder,
  request,
  onApply,
  onAnswer,
  onDiscard,
}: {
  id: string;
  heading: string;
  label: string;
  placeholder: string;
  request: (prompt: string) => Promise<FormPromptDraft>;
  onApply: (draft: FormPromptDraft) => void;
  onAnswer: (field: string, value: string) => void;
  onDiscard: () => void;
}) {
  const [prompt, setPrompt] = useState('');
  const [result, setResult] = useState<FormPromptDraft | null>(null);
  const [answered, setAnswered] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);

  const ask = useMutation({
    mutationFn: () => request(prompt),
    onSuccess: (draft) => {
      setError(null);
      setResult(draft);
      setAnswered({});
      if (draft.suggestionId) onApply(draft);
    },
    onError: (caught) => setError(caught instanceof ApiError ? caught.message : t("The draft could not be made.")),
  });

  const answer = (field: string, value: string) => {
    setAnswered({ ...answered, [field]: value });
    onAnswer(field, value);
  };

  return (
    <section className="mt-4 rounded-md border border-border bg-surface p-4" aria-labelledby={`${id}-heading`}>
      <h3 id={`${id}-heading`} className="m-0 text-sm font-semibold">
        {heading}
      </h3>
      <p className="mt-1 text-sm text-content-secondary">
        {t("AI fills in what your sentence states. You check every field before saving. Nothing is saved until you save.")}
      </p>
      <label className="sr-only" htmlFor={`${id}-text`}>
        {label}
      </label>
      <textarea
        id={`${id}-text`}
        rows={2}
        value={prompt}
        onChange={(event) => setPrompt(event.target.value)}
        placeholder={placeholder}
        className="mt-2 w-full rounded-sm border border-border-strong px-3 py-2 text-sm"
      />
      <div className="mt-2 flex flex-wrap gap-2">
        <button
          type="button"
          disabled={ask.isPending || prompt.trim().length < 10}
          onClick={() => ask.mutate()}
          className="rounded-sm bg-ag-purple px-3 py-1.5 text-sm text-content-inverse disabled:opacity-60"
        >
          {ask.isPending ? t("Reading…") : t("Fill in the form")}
        </button>
        {result?.suggestionId && (
          <button
            type="button"
            onClick={() => {
              onDiscard();
              setResult(null);
            }}
            className="rounded-sm border border-border-strong px-3 py-1.5 text-sm"
          >
            {t("Discard AI draft")}
          </button>
        )}
      </div>

      {error && (
        <p role="alert" className="mb-0 mt-3 text-sm text-tone-critical">
          {error}
        </p>
      )}
      {result && !result.suggestionId && (
        <p role="status" className="mb-0 mt-3 text-sm text-content-secondary">
          {result.unavailableReason ?? t("No draft this time. Fill the form in by hand.")}
        </p>
      )}

      {result?.suggestionId && (
        <div className="mt-3 rounded-sm bg-tone-pending-surface p-3 text-sm" role="status">
          <p className="m-0">
            <strong>{t("AI draft applied.")}</strong>{' '}{t("Check every field — it is the AI’s reading of your sentence, and you are the one who saves it.")}
          </p>
          {result.notStated.length > 0 && (
            <p className="mb-0 mt-2">{t("Not stated, so left for you to enter:")}{' '}{result.notStated.join(', ')}.</p>
          )}
          {result.clarifications.length > 0 && (
            <ul className="m-0 mt-3 list-none space-y-2 p-0">
              {result.clarifications.map((question) => (
                <li key={question.field}>
                  <label className="flex flex-col gap-1">
                    <span>{question.question}</span>
                    {question.options ? (
                      <select
                        value={answered[question.field] ?? ''}
                        onChange={(event) => answer(question.field, event.target.value)}
                        className="rounded-sm border border-border-strong px-2 py-1"
                      >
                        <option value="">{t("Choose…")}</option>
                        {question.options.map((option) => (
                          <option key={option.value} value={option.value}>
                            {t(option.label)}
                          </option>
                        ))}
                      </select>
                    ) : (
                      <input
                        type="text"
                        value={answered[question.field] ?? ''}
                        onChange={(event) => answer(question.field, event.target.value)}
                        className="rounded-sm border border-border-strong px-2 py-1"
                      />
                    )}
                  </label>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  );
}
